import { createContext, useContext, useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const sessionRevision=useRef(0);

  useEffect(() => {
    const revision=++sessionRevision.current;
    (async () => {
      try {
        const { data } = await api.get("/auth/me",{params:{_session:Date.now()},headers:{"Cache-Control":"no-cache"}});
        // Backend returns null for anonymous visitors (200 OK).
        if(revision===sessionRevision.current) setUser(data || null);
      } catch (_e) {
        if(revision===sessionRevision.current) setUser(null);
      } finally {
        if(revision===sessionRevision.current) setChecking(false);
      }
    })();
  }, []);

  // ── Auto-detect timezone once, silently, if the profile has none.
  // Uses `Intl.DateTimeFormat().resolvedOptions().timeZone` (all modern
  // browsers). Runs whenever `user` becomes non-null; the backend's
  // /auth/me/language-prefs is our source of truth for whether a tz is
  // already saved. Best-effort: any failure is silent — the user can
  // still set their tz manually on /account/settings.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data: prefs } = await api.get("/auth/me/language-prefs");
        if (prefs?.timezone) return;                  // already set
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!detected || detected === "UTC") return;  // don't overwrite
        await api.patch("/auth/me/timezone", { timezone: detected });
      } catch (_e) { /* silent — user can set on /account/settings */ }
    })();
  }, [user?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function login(email, password, rememberComputer = false) {
    ++sessionRevision.current;
    try {
      // Datacenter-IP VPN gate: if the current URL (or a previously
      // visited one) carries `?reviewer=TOKEN`, forward that token so
      // the backend can short-circuit the IP block. Harmless in prod.
      let reviewer = "";
      try {
        reviewer =
          new URLSearchParams(window.location.search).get("reviewer") ||
          window.sessionStorage.getItem("hs_reviewer") ||
          "";
        if (reviewer) {
          window.sessionStorage.setItem("hs_reviewer", reviewer);
        }
      } catch (_) { /* no window — SSR / tests */ }
      const url = reviewer
        ? `/auth/login?reviewer=${encodeURIComponent(reviewer)}`
        : "/auth/login";
      await api.post(url, { email, password, remember_computer: rememberComputer });
      const {data}=await api.get("/auth/me",{params:{_session:Date.now()},headers:{"Cache-Control":"no-cache"}});
      if(!data) throw Error("Sign-in succeeded but the session was not saved. Please allow site cookies and try again.");
      setUser(data);setChecking(false);
      return { ok: true, user: data };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Preserve the structured 403 payload (vpn_blocked / ip_blocked)
      // so LoginPage can render the friendly full-screen notice
      // instead of a raw toast.
      if (detail && typeof detail === "object" && detail.code) {
        return { ok: false, blockDetail: detail,
                 error: detail.message || detail.reason || "Access denied" };
      }
      return { ok: false, error: formatApiError(detail) || e.message };
    }
  }

  async function register({ email, password, name, phone }) {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, phone });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        return { ok: false, blockDetail: detail,
                 error: detail.message || detail.reason || "Access denied" };
      }
      return { ok: false, error: formatApiError(detail) || e.message };
    }
  }

  async function logout() {
    ++sessionRevision.current;
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    setUser(null);
  }

  /** Re-check the current session from /auth/me — useful after
   *  a magic-link / OAuth callback sets cookies from a URL flow. */
  async function refresh() {
    const revision=++sessionRevision.current;
    try {
      const { data } = await api.get("/auth/me",{params:{_session:Date.now()},headers:{"Cache-Control":"no-cache"}});
      if(revision===sessionRevision.current){setUser(data || null);setChecking(false);}
      return data || null;
    } catch (_e) {
      if(revision===sessionRevision.current){setUser(null);setChecking(false);}
      return null;
    }
  }

  // Convenience role flags. `agency_owner` (full access across tenants) and
  // legacy `admin` are treated identically. `business_admin` (vendor) is
  // scoped to a single business and never sees /agency or the tenant switcher.
  const role = user?.role || null;
  const isAgencyOwner = role === "agency_owner" || role === "admin";
  const isVendor = role === "business_admin";
  const isStaff = isAgencyOwner || isVendor;

  return (
    <AuthContext.Provider
      value={{
        user, checking, login, register, logout, refresh,
        role, isAgencyOwner, isVendor, isStaff,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
